import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import Tab from '@mui/material/Tab'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { StyledTabPanel } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import {
  useBlockScene,
  useElementLabel,
  useElementType,
  useResultsInvalidation,
} from '@editorHooks'
import {
  CollapsibleAssemblyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleGeometryBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  CollapsibleSplitBoxSection,
  DocumentationItem,
} from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getVerticalTransmissionGraph, getModel, getProjectFiles } from '@queries'
import { createPurlin, deletePurlin, updatePurlin as updatePurlinReq } from '@mutations'
import { buildErrorMessage, documentationRootGuid } from 'src/constants'
import { useElementHistory } from 'src/hooks'
import SingleElementCSForm from '../../../../../../components/SingleElementCSForm'
import PlanSettingsForm from '../../../PlanSettingsForm'
import SplitLineElementForm from '../../../SplitLineElementForm'
import GeometryForm from './GeometryForm'
import PurlinFormBase from './PurlinFormBase'
import { purlinTabs } from './tabs'

export type PurlinGeometryType =
  | 'x-or-y-no-z-elevation'
  | 'xy-diagonal-no-z-elevation'
  | 'x-or-y-z-elevation'
  | 'xy-diagonal-z-elevation'

interface ToggleProps {
  geometryType: PurlinGeometryType
  setGeometryType: (type: PurlinGeometryType) => void
}

const PurlinGeometryTypeToggle = ({ geometryType, setGeometryType }: ToggleProps) => {
  const getXYType = (type: PurlinGeometryType) => {
    if (type === 'x-or-y-no-z-elevation' || type === 'x-or-y-z-elevation') return 'xy'
    if (type === 'xy-diagonal-no-z-elevation' || type === 'xy-diagonal-z-elevation')
      return 'xy_diagonal'
    return 'xy'
  }

  const getZType = (type: PurlinGeometryType) => {
    if (type === 'x-or-y-no-z-elevation' || type === 'xy-diagonal-no-z-elevation')
      return 'horizontal'
    if (type === 'x-or-y-z-elevation' || type === 'xy-diagonal-z-elevation') return 'elevated'
    return 'horizontal'
  }

  const handleXYChange = (newXYType: string) => {
    const currentZType = getZType(geometryType)
    const newType =
      newXYType === 'xy'
        ? currentZType === 'horizontal'
          ? 'x-or-y-no-z-elevation'
          : 'x-or-y-z-elevation'
        : currentZType === 'horizontal'
        ? 'xy-diagonal-no-z-elevation'
        : 'xy-diagonal-z-elevation'
    setGeometryType(newType as PurlinGeometryType)
  }

  const handleZChange = (newZType: string) => {
    const currentXYType = getXYType(geometryType)
    const newType =
      currentXYType === 'xy'
        ? newZType === 'horizontal'
          ? 'x-or-y-no-z-elevation'
          : 'x-or-y-z-elevation'
        : newZType === 'horizontal'
        ? 'xy-diagonal-no-z-elevation'
        : 'xy-diagonal-z-elevation'
    setGeometryType(newType as PurlinGeometryType)
  }

  return (
    <Stack direction="column" spacing={1} alignItems="center" sx={{ width: '100%' }}>
      <ToggleButtonGroup
        value={getXYType(geometryType)}
        exclusive
        onChange={(_, newValue) => newValue && handleXYChange(newValue)}
        aria-label="purlin xy geometry type"
        fullWidth
        size="small"
      >
        <ToggleButton value="xy" aria-label="xy purlin" fullWidth sx={{ paddingY: 0 }}>
          X oder Y
        </ToggleButton>
        <ToggleButton
          value="xy_diagonal"
          aria-label="xy diagonal purlin"
          fullWidth
          sx={{ paddingY: 0 }}
        >
          X/Y Diagonal
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        value={getZType(geometryType)}
        exclusive
        onChange={(_, newValue) => newValue && handleZChange(newValue)}
        aria-label="purlin z geometry type"
        fullWidth
        size="small"
        sx={{ padding: 0 }}
      >
        <ToggleButton
          value="horizontal"
          aria-label="horizontal purlin"
          fullWidth
          sx={{ paddingY: 0 }}
        >
          Horizontal
        </ToggleButton>
        <ToggleButton value="elevated" aria-label="elevated purlin" fullWidth sx={{ paddingY: 0 }}>
          In z geneigt
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography noWrap>
        {geometryType === 'x-or-y-no-z-elevation'
          ? 'z. Bsp. horizontale Pfette oder Unterzug'
          : geometryType === 'xy-diagonal-no-z-elevation'
          ? 'z. Bsp. xy diagonale Pfette oder diagonaler Unterzug'
          : geometryType === 'x-or-y-z-elevation'
          ? 'z. Bsp. x-Pfette mit z-Neigung'
          : 'z. Bsp. xyz diagonale Pfette, Sparren, Grat- oder Kehlsparren'}
      </Typography>
    </Stack>
  )
}

interface Props {
  selectedElement: string
  onClose: () => void
  interactionMode: '2D' | '3D'
}

const PurlinForm = ({ selectedElement, onClose, interactionMode }: Props): ReactElement | null => {
  const [geometryType, setGeometryType] = useState<PurlinGeometryType>('x-or-y-no-z-elevation')
  const elementType = useElementType(selectedElement)

  const { purlins } = useModelStore(state => state.model)
  const removePurlin = useModelStore(state => state.removePurlin)
  const updatePurlin = useModelStore(state => state.updatePurlin)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()

  const purlin = useMemo(() => {
    return find(purlins, ['guid', selectedElement]) as PlanLineElement
  }, [purlins, selectedElement])

  const { setStale: setPurlinStale } = useElementHistory({
    element: purlin,
    getCurrentElement: () => {
      const purlins = useModelStore.getState().model.purlins
      return find(purlins, ['guid', selectedElement]) as PlanLineElement
    },
    removeElement: purlin => removePurlin(purlin.guid),
    resetElement: purlin => updatePurlin(purlin),
    dependencies: [selectedElement],
  })

  useStructuralPlanningFormEsc(() => {
    // purlin has to be saved otherwise remove
    if (purlin?.is_local) {
      removePurlin(purlin.guid)
    }
    onClose()
  }, !!selectedElement)

  const { mutateAsync, isLoading } = useMutation(
    (purlin: PlanLineElement) => createPurlin.request(projectId, purlin),
    {
      onSuccess: async (data: PlanLineElement) => {
        setPurlinStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Pfette erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Pfette'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: update, isLoading: isUpdating } = useMutation(
    (purlin: PlanLineElement) => updatePurlinReq.request(projectId, purlin),
    {
      onSuccess: async (data: PlanLineElement) => {
        setPurlinStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Pfette erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Pfette'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (purlinGuid: string) => deletePurlin.request(projectId, purlinGuid),
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Pfette erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Pfette'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isLoading || isDeleting || isUpdating)

  if (!purlin) return null

  if (purlin.is_local) {
    return (
      <Stack direction="column" spacing={1} alignItems={'center'} p={1}>
        <GeometryForm
          purlin={purlin}
          handleSubmit={purlin => mutateAsync(purlin)}
          handleDelete={() => removePurlin(purlin.guid)}
          geometryType={geometryType}
          geometryTypeToggle={
            <PurlinGeometryTypeToggle
              geometryType={geometryType}
              setGeometryType={setGeometryType}
            />
          }
          interactionMode={interactionMode}
          isSubmitting={isLoading}
          showCreateLabel
        />
      </Stack>
    )
  }

  return (
    <PurlinFormBase
      initialTabValue={purlinTabs.geometry.tab}
      selectedElement={purlin.guid}
      tab={<Tab value={purlinTabs.geometry.tab} label={purlinTabs.geometry.label} />}
    >
      <StyledTabPanel value={purlinTabs.geometry.tab} data-cy="tab-geometry">
        <Stack direction="column" spacing={1}>
          <StructuralPlanningElementFormHeader
            elementGuid={selectedElement}
            label={getLabel(selectedElement)}
            onClose={onClose}
          />

          <CollapsibleAssemblyBoxSection label="Querschnitt/Aufbau" defaultExpanded={true}>
            <SingleElementCSForm
              selectedElement={selectedElement}
              elementType={elementType as ElementTypes}
            />
          </CollapsibleAssemblyBoxSection>

          <CollapsibleGeometryBoxSection defaultExpanded={true}>
            <GeometryForm
              purlin={purlin}
              handleSubmit={purlin => update(purlin)}
              handleDelete={() => handleDelete(purlin.guid)}
              isDeleting={isDeleting}
              geometryType={geometryType}
              geometryTypeToggle={
                <PurlinGeometryTypeToggle
                  geometryType={geometryType}
                  setGeometryType={setGeometryType}
                />
              }
              interactionMode={interactionMode}
              isSubmitting={isUpdating}
            />
          </CollapsibleGeometryBoxSection>

          <CollapsibleSettingsBoxSection defaultExpanded={false}>
            <PlanSettingsForm element={purlin} setStale={setPurlinStale} />
          </CollapsibleSettingsBoxSection>

          <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
            <SplitLineElementForm
              elementGuid={selectedElement}
              elementType="purlins"
              interactionMode={interactionMode}
              setElementStale={setPurlinStale}
            />
          </CollapsibleSplitBoxSection>

          <CollapsibleDocumentationBoxSection defaultExpanded={false}>
            <DocumentationItem itemGuid={selectedElement} />
          </CollapsibleDocumentationBoxSection>

          <CollapsibleProblemsBoxSection elementGuid={selectedElement} defaultExpanded={true} />
        </Stack>
      </StyledTabPanel>
    </PurlinFormBase>
  )
}

export default PurlinForm
